import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Paper, Button } from '@mui/material';
import { FaInstagram, FaTelegramPlane, FaVk } from 'react-icons/fa';

// --- ОБНОВЛЕННЫЕ ДАННЫЕ САЛОНОВ ---
const branches = {
  revolyutsionnaya: {
    key: 'revolyutsionnaya',
    label: 'Студия на Революционной',
    city: 'Самара',
    streetLine: 'ул. Революционная, 3',
    title: 'Lindos Beauty — Революционная',
    phone: '+7 (917) 030-03-08',
    workHours: 'Ежедневно с 10:00 до 21:00',
    // Координаты: [долгота, широта]
    coords: [50.1627, 53.2168], // [reference:0][reference:1]
    routeUrl: 'https://yandex.ru/maps/?text=Самара, ул. Революционная, 3',
  },
  dachnaya: {
    key: 'dachnaya',
    label: 'Студия на Дачной',
    city: 'Самара',
    streetLine: 'ул. Дачная, 27А',
    title: 'Lindos Beauty — Дачная',
    phone: '+7 (917) 030-03-08',
    workHours: 'Ежедневно с 10:00 до 21:00',
    // Координаты: [долгота, широта]
    coords: [50.1436, 53.1982], // [reference:2]
    routeUrl: 'https://yandex.ru/maps/?text=Самара, ул. Дачная, 27А',
  },
};

const MapSection = ({ compact = false }) => {
  const [activeBranch, setActiveBranch] = useState('revolyutsionnaya'); // По умолчанию выбран первый
  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState(false);

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  const currentBranch = branches[activeBranch];

  useEffect(() => {
    const apiKey = process.env.REACT_APP_YMAPS_API_KEY;

    if (!apiKey) {
      setMapError(true);
      return;
    }

    if (window.ymaps3) {
      setMapReady(true);
      return;
    }

    const existingScript = document.querySelector(
      'script[data-yandex-maps-v3="true"]'
    );

    const onLoad = () => {
      if (window.ymaps3) {
        setMapReady(true);
      } else {
        setMapError(true);
      }
    };

    const onError = () => {
      setMapError(true);
    };

    if (existingScript) {
      existingScript.addEventListener('load', onLoad);
      existingScript.addEventListener('error', onError);

      return () => {
        existingScript.removeEventListener('load', onLoad);
        existingScript.removeEventListener('error', onError);
      };
    }

    const script = document.createElement('script');
    script.src = `https://api-maps.yandex.ru/v3/?apikey=${apiKey}&lang=ru_RU`;
    script.async = true;
    script.dataset.yandexMapsV3 = 'true';
    script.addEventListener('load', onLoad);
    script.addEventListener('error', onError);

    document.head.appendChild(script);

    return () => {
      script.removeEventListener('load', onLoad);
      script.removeEventListener('error', onError);
    };
  }, []);

  useEffect(() => {
    if (!mapReady || !mapContainerRef.current || mapRef.current || !window.ymaps3) return;

    let destroyed = false;

    const initMap = async () => {
      try {
        await window.ymaps3.ready;
        if (destroyed) return;

        const { YMap, YMapDefaultSchemeLayer, YMapDefaultFeaturesLayer, YMapMarker } =
          window.ymaps3;

        const initialBranch = branches[activeBranch];

        const map = new YMap(mapContainerRef.current, {
          location: {
            center: initialBranch.coords,
            zoom: 13,
          },
        });

        map.addChild(new YMapDefaultSchemeLayer());
        map.addChild(new YMapDefaultFeaturesLayer());

        const markerElement = document.createElement('div');
        markerElement.style.width = '18px';
        markerElement.style.height = '18px';
        markerElement.style.borderRadius = '50%';
        markerElement.style.background = '#14245f';
        markerElement.style.border = '3px solid #d8b15b';
        markerElement.style.boxShadow = '0 6px 18px rgba(20,36,95,0.28)';

        const marker = new YMapMarker(
          {
            coordinates: initialBranch.coords,
          },
          markerElement
        );

        map.addChild(marker);

        mapRef.current = map;
        markerRef.current = marker;
      } catch (e) {
        setMapError(true);
      }
    };

    initMap();

    return () => {
      destroyed = true;
      if (mapRef.current) {
        mapRef.current.destroy();
        mapRef.current = null;
        markerRef.current = null;
      }
    };
  }, [mapReady, activeBranch]);

  useEffect(() => {
    if (!mapRef.current || !markerRef.current || !window.ymaps3) return;

    const { YMapMarker } = window.ymaps3;
    const branch = branches[activeBranch];

    mapRef.current.update({
      location: {
        center: branch.coords,
        zoom: 15,
        duration: 300,
      },
    });

    try {
      mapRef.current.removeChild(markerRef.current);
    } catch {}

    const markerElement = document.createElement('div');
    markerElement.style.width = '18px';
    markerElement.style.height = '18px';
    markerElement.style.borderRadius = '50%';
    markerElement.style.background = '#14245f';
    markerElement.style.border = '3px solid #d8b15b';
    markerElement.style.boxShadow = '0 6px 18px rgba(20,36,95,0.28)';

    const nextMarker = new YMapMarker(
      {
        coordinates: branch.coords,
      },
      markerElement
    );

    mapRef.current.addChild(nextMarker);
    markerRef.current = nextMarker;
  }, [activeBranch]);

  return (
    <Box
      sx={{
        py: compact ? 0 : { xs: 6, md: 8 },
        background: compact
          ? 'transparent'
          : `
          radial-gradient(circle at 86% 24%, rgba(201,161,74,0.10) 0%, transparent 20%),
          radial-gradient(circle at 14% 78%, rgba(20,36,95,0.08) 0%, transparent 24%),
          linear-gradient(180deg, #f3f3f1 0%, #ecece9 100%)
        `,
      }}
    >
      {!compact && (
        <>
          <Typography
            variant="h3"
            textAlign="center"
            gutterBottom
            sx={{
              color: '#14245f',
              mb: 1.5,
              fontFamily: '"Playfair Display", serif',
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '2.8rem' },
            }}
          >
            Как нас найти
          </Typography>

          <Typography
            textAlign="center"
            sx={{
              mb: 4,
              color: '#667085',
              maxWidth: 640,
              mx: 'auto',
              lineHeight: 1.8,
            }}
          >
            Выберите студию — и увидите отметку на карте, адрес и контакты.
          </Typography>
        </>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: compact ? 'flex-start' : 'center',
          flexWrap: 'wrap',
          gap: 1.5,
          mb: 3,
        }}
      >
        {Object.values(branches).map((branch) => {
          const active = activeBranch === branch.key;
          return (
            <Button
              key={branch.key}
              onClick={() => setActiveBranch(branch.key)}
              sx={{
                textTransform: 'none',
                fontSize: 14,
                px: { xs: 2.2, md: 3.5 },
                py: 1.1,
                borderRadius: 999,
                border: active
                  ? '1px solid rgba(201,161,74,0.36)'
                  : '1px solid rgba(20,36,95,0.14)',
                background: active
                  ? 'linear-gradient(135deg, #14245f 0%, #1a237e 100%)'
                  : 'rgba(255,255,255,0.76)',
                color: active ? '#fff' : '#14245f',
                fontWeight: 700,
              }}
            >
              {branch.label}
            </Button>
          );
        })}
      </Box>

      <Paper
        elevation={0}
        sx={{
          position: 'relative',
          borderRadius: 4,
          overflow: 'hidden',
          minHeight: { xs: 620, md: 460 },
          border: '1px solid rgba(20,36,95,0.08)',
          boxShadow:
            '0 18px 40px rgba(0,0,0,0.08), 0 6px 18px rgba(20,36,95,0.05)',
          backgroundColor: '#fff',
        }}
      >
        <Box
          sx={{
            width: '100%',
            height: { xs: 360, md: 460 },
            background:
              mapError || !mapReady
                ? 'linear-gradient(180deg, #eef1f5 0%, #e4e8ee 100%)'
                : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            ref={mapContainerRef}
            sx={{
              width: '100%',
              height: '100%',
              display: mapError ? 'none' : 'block',
            }}
          />

          {mapError && (
            <Box sx={{ textAlign: 'center', px: 3 }}>
              <Typography sx={{ color: '#14245f', fontWeight: 700, mb: 1 }}>
                Карта временно недоступна
              </Typography>
              <Typography sx={{ color: '#667085', maxWidth: 420 }}>
                Добавь валидный ключ Яндекс Карт и HTTP Referer для localhost.
              </Typography>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            position: { xs: 'relative', md: 'absolute' },
            right: { md: 28 },
            top: { md: 28 },
            bgcolor: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: { xs: 0, md: 4 },
            boxShadow: {
              xs: 'none',
              md: '0 18px 45px rgba(0,0,0,0.16)',
            },
            width: { xs: '100%', md: 360 },
            p: { xs: 2.2, md: 3 },
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 800,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: '#b2872f',
            }}
          >
            Контакты
          </Typography>

          <Typography
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontSize: 24,
              fontWeight: 700,
              color: '#14245f',
              lineHeight: 1.2,
            }}
          >
            {currentBranch.title}
          </Typography>

          <Typography sx={{ fontSize: 14, color: '#667085', lineHeight: 1.7 }}>
            {currentBranch.city}, {currentBranch.streetLine}
          </Typography>

          <Typography sx={{ fontSize: 14, color: '#667085' }}>
            {currentBranch.workHours}
          </Typography>

          <Typography
            sx={{
              fontSize: 20,
              fontWeight: 700,
              color: '#14245f',
            }}
          >
            {currentBranch.phone}
          </Typography>

          <Typography sx={{ fontSize: 13, color: '#667085' }}>
            Звоните или оставьте заявку онлайн
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, mt: 0.5, mb: 0.5 }}>
            {[FaVk, FaTelegramPlane, FaInstagram].map((Icon, index) => (
              <Box
                key={index}
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: '50%',
                  background: 'rgba(20,36,95,0.05)',
                  color: '#14245f',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid rgba(20,36,95,0.08)',
                }}
              >
                <Icon size={15} />
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row', md: 'column' },
              gap: 1.2,
              mt: 1,
            }}
          >
            <Button
              variant="outlined"
              fullWidth
              onClick={() => window.open(currentBranch.routeUrl, '_blank')}
              sx={{
                textTransform: 'none',
                borderRadius: 999,
                py: 1.1,
                fontWeight: 700,
                borderColor: 'rgba(20,36,95,0.18)',
                color: '#14245f',
              }}
            >
              Построить маршрут
            </Button>

            <Button
              variant="contained"
              fullWidth
              href="/contacts"
              sx={{
                textTransform: 'none',
                borderRadius: 999,
                py: 1.1,
                background:
                  'linear-gradient(135deg, #14245f 0%, #1a237e 58%, #2b3ea7 100%)',
                color: '#fff',
                fontWeight: 700,
              }}
            >
              Записаться
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default MapSection;